import React, { useMemo, useReducer } from 'react';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppPaths from '../../AppPaths';
import Button from '../../components/Button';
import Chooser from '../../components/Chooser';
import ImageGallery from '../../components/ImageGallery/ImageGallery';
import useGiftCardProducts from '../../hooks/useGiftCardProducts';
import { expectUnreachable } from '../../lib/util';
import DeliveryDetails from './components/DeliveryDetails/DeliveryDetails';
import styles from './GiftCard.module.scss';
import GiftCardContext, {
  emptyGiftCardState,
  GiftCardAction,
  IGiftCardState,
  setErrors,
  setSelectedId,
} from './GiftCardContext';
import { GIFT_CARD_IMAGES } from './GiftCardImages.constant';
import useCheckout from './useCheckout';
import useTracking from './useTracking';

/**
 * Configure details for the gift card.
 */
export default function GiftCard() {
  const { t } = useTranslation();
  const giftCardProducts = useGiftCardProducts();
  const initialState: IGiftCardState = useMemo((): IGiftCardState => {
    return {
      ...emptyGiftCardState,
      giftCardProducts,
    };
  }, [giftCardProducts]);

  const [giftCardState, giftCardDispatch] = useReducer((state: IGiftCardState, action: GiftCardAction) => {
    if (action.type === 'SetSelectedId') {
      return { ...state, selectedId: action.newSelectedId };
    } else if (action.type === 'UpdateGiftCardDelivery') {
      return {
        ...state,
        delivery: { ...state.delivery, ...action.newValues },
      };
    } else if (action.type === 'SetErrors') {
      return { ...state, errors: action.errors };
    } else if (action.type === 'ClearError') {
      const { [action.errorName]: ignoredValue, ...newErrors } = state.errors;
      return { ...state, errors: newErrors };
    } else if (action.type === 'SetDeliverAtIsSet') {
      return { ...state, deliverAtIsSet: action.newValue };
    } else {
      expectUnreachable(action);
      return state;
    }
  }, initialState);

  const canContinue = giftCardState.selectedId !== null;

  const product = useMemo(
    () => giftCardProducts.find((p) => giftCardState.selectedId === p.id),
    [giftCardProducts, giftCardState.selectedId],
  );

  // Analytics
  useTracking(product);

  const { addedToBag, addToBag } = useCheckout((e) => {
    giftCardDispatch(setErrors(e));
  });

  if (addedToBag) {
    return <Redirect to={AppPaths.Bag} />;
  }

  return (
    <GiftCardContext.Provider value={{ giftCardState, giftCardDispatch }}>
      <div className={styles.giftCardView}>
        <div className={styles.imageCarousel}>
          <ImageGallery media={GIFT_CARD_IMAGES} />
        </div>

        <div className={styles.giftCardDetails}>
          <h1>{t('fi_gift_card_uppercase')}</h1>
          <p>{t('select_the_gift_card_youd_like_to_send_well_email_the_recipient_a_link_to_redeem_their_gift')}</p>
          <p className={styles.finePrint}>
            {t('gift_cards_can_be_used_for_any_collar_kits_or_accessories_on_tryfi_com')}
          </p>
          <Chooser
            groups={giftCardProducts.map((giftCardProduct) => {
              return {
                options: [
                  {
                    value: giftCardProduct.id,
                    content: (
                      <div className={styles.giftCardChooser}>
                        <p className={styles.giftCardChooserTitle}>{t('gift_card')}</p>
                        <p className={styles.giftCardChooserDenomination}>
                          <strong>{giftCardProduct.priceInCents / 100}</strong>
                        </p>
                        <p className={styles.giftCardChooserDescription}>{giftCardProduct.nameForBanner}</p>
                      </div>
                    ),
                  },
                ],
              };
            })}
            selectedOption={giftCardState.selectedId}
            onSelect={(useGiftCard) => giftCardDispatch(setSelectedId(useGiftCard))}
          />
          <DeliveryDetails />

          <Button
            className={styles.continueButton}
            onClick={() => {
              if (canContinue && product) {
                addToBag(product, giftCardState);
                return <Redirect to={AppPaths.Bag} />;
              } else {
                console.error('cannot continue!', canContinue, product);
              }
            }}
            disabled={!canContinue}
          >
            {t('continue')}
          </Button>
        </div>
      </div>
    </GiftCardContext.Provider>
  );
}
